import { z } from 'zod';
import { jsonObject, localeSchema, timestamp, uuid } from './common.js';
import { listQueryInput, paginated } from './list.js';
import { auditAction, auditEventSchema } from './cross.js';
import { userPublicSchema, userRole, userStatus, userTier } from './user.js';
import { subscriptionSchema } from './credit.js';
import { moderationStatus } from './share.js';
import { previewDescriptor } from './preview.js';
import { policyCategory } from './errors.js';
import { jobType } from './job.js';
import { landingContentSchema } from './content.js';
/**
 * Admin (§4, F53). `contact_submissions` — also carries DMCA takedowns via
 * `submissionType='dmca'` (no separate entity, §26).
 */
export const submissionType = z.enum(['contact', 'dmca']);
export const submissionStatus = z.enum(['new', 'in_progress', 'handled']);
export const contactSubmissionSchema = z.object({
    id: uuid,
    submissionType,
    name: z.string().min(1).max(200),
    email: z.string().email(),
    subject: z.string().max(300).nullable(),
    message: z.string().min(1),
    status: submissionStatus,
    handledByUserId: uuid.nullable(),
    createdAt: timestamp,
    updatedAt: timestamp,
});
/** Public contact/DMCA form DTO (F38/F37) — client + server. */
export const contactSubmissionInput = z.object({
    submissionType: submissionType.default('contact'),
    name: z.string().trim().min(1, 'errors:contact.name.required').max(200),
    email: z.string().email('errors:contact.email.invalid'),
    subject: z.string().trim().max(300).optional(),
    message: z.string().trim().min(1, 'errors:contact.message.required').max(10000),
});
/**
 * Admin op DTOs (F53–F61, F68, F71). ADR (PLAN.md): the admin surface is a
 * role-gated section of the `@xdocs/web` SPA — NOT the Supabase-hosted staff
 * app IMPLEMENTATION_PLAN.md's §5/§6 originally specified — so every
 * `admin.*` action below is a normal WS op, `requireRole('admin')`-guarded
 * server-side (moderator-scoped ops live in `./moderation.ts`
 * `queue.*`/`moderation.takedown`, PUBLIC-content only). Every mutating op
 * here writes exactly one `audit_events` row per logical change via the
 * single `@xdocs/core` writer (§15 "EVERY sensitive mutation... writes
 * audit_events") — never a UI-only gate.
 */
// --- F54 users management ----------------------------------------------------
export const adminUserSortColumns = [
    'createdAt',
    'email',
    'displayName',
    'tier',
    'role',
    'status',
];
export const adminUserListQuery = listQueryInput(adminUserSortColumns, {
    defaultSort: 'createdAt',
}).extend({
    role: userRole.optional(),
    tier: userTier.optional(),
    status: userStatus.optional(),
});
/** `admin.users.list` row — the safe public projection, never a password hash (F54 AC). */
export const adminUserListResult = paginated(userPublicSchema);
export const adminUserGetInput = z.object({ userId: uuid });
/**
 * `admin.users.get` — the F54 detail-screen header. Deeper read views
 * (credit ledger, sessions, full invoice history) are their OWN paginated
 * ops the screen composes (`creditLedgerListQuery` scoped by an admin
 * override, `session.list` equivalent, `invoiceListQuery`) rather than one
 * giant nested payload.
 */
export const adminUserDetail = z.object({
    user: userPublicSchema,
    subscription: subscriptionSchema.nullable(),
    activeSessionCount: z.number().int().nonnegative(),
});
/**
 * `admin.users.update` — tier/role/status are independent axes (§15: never
 * conflate `users.role` RBAC / `users.tier` billing / `shares.share_role`);
 * at least one must be present. The handler writes ONE `audit_events` row
 * per changed field (`user.tier_change`/`user.role_change`/`user.suspend`),
 * not one row per call. `reason` is REQUIRED (F54 AC: "every sensitive op
 * requires a reason for the audit").
 */
export const adminUserUpdateInput = z
    .object({
    userId: uuid,
    tier: userTier.optional(),
    role: userRole.optional(),
    status: userStatus.optional(),
    reason: z.string().trim().min(1, 'errors:admin.reason.required').max(1000, 'errors:admin.reason.tooLong'),
})
    .refine((v) => v.tier !== undefined || v.role !== undefined || v.status !== undefined, {
    message: 'errors:admin.user.update.empty',
    path: ['tier'],
});
export const adminUserUpdateResult = userPublicSchema;
/** `admin.users.creditAdjust` — writes `credit_ledger` (`reason='admin_grant'`), audited `user.credit_adjust`. `delta` may be negative (a correction) but never zero. */
export const adminCreditAdjustInput = z.object({
    userId: uuid,
    delta: z.number().int().refine((n) => n !== 0, { message: 'errors:admin.creditAdjust.nonZero' }),
    reason: z.string().trim().min(1, 'errors:admin.reason.required').max(1000, 'errors:admin.reason.tooLong'),
});
export const adminCreditAdjustResult = z.object({ userId: uuid, newBalance: z.number().int() });
/** `admin.users.forceLogout` — revokes every session, pushing `session-revoked` (`./comms.ts`) on `user:{userId}` per session (F54 AC). */
export const adminForceLogoutInput = z.object({
    userId: uuid,
    reason: z.string().trim().min(1, 'errors:admin.reason.required').max(1000, 'errors:admin.reason.tooLong'),
});
export const adminForceLogoutResult = z.object({
    userId: uuid,
    revokedCount: z.number().int().nonnegative(),
});
// --- F58 view-as-user (impersonation) -----------------------------------------
/** Hard TTL (§15 "time-boxed... revocable"); mirrors `impersonation_sessions.expiresAt` (`@xdocs/db`, additive). */
export const IMPERSONATION_TTL_MINUTES = 30;
export const impersonationStartInput = z.object({
    targetUserId: uuid,
    reason: z.string().trim().min(1, 'errors:admin.reason.required').max(1000, 'errors:admin.reason.tooLong'),
});
/**
 * `impersonation.start` result. `accessToken` is a fresh short-lived token
 * scoped to the TARGET user but carrying the impersonation claim the WS
 * gateway checks on every op (§15 "WRITE-BLOCKED... fully audited"): reads
 * proceed as the target, writes are rejected `FORBIDDEN` server-side unless
 * explicitly whitelisted, never a UI-only gate. `expiresAt` drives the
 * mandatory app-shell banner countdown (F58 AC: "hard time-box even if the
 * tab stays open").
 */
export const impersonationStartResult = z.object({
    impersonationSessionId: uuid,
    targetUserId: uuid,
    accessToken: z.string().min(1),
    expiresAt: timestamp,
});
/** `impersonation.end` — explicit "Exit view-as", or the TTL auto-expiry path taking the same shape server-side. Both are audited (`impersonation.end`, §15). */
export const impersonationEndInput = z.object({ impersonationSessionId: uuid });
export const impersonationEndResult = z.object({
    impersonationSessionId: uuid,
    endedAt: timestamp,
});
// --- F59 audit-log viewer ------------------------------------------------------
export const adminAuditSortColumns = ['createdAt'];
/**
 * `admin.audit.list` — the staff-facing, UNFILTERED read over `audit_events`
 * (sibling to the user-scoped `./activity.ts` `activityListQuery`).
 * `impersonationOnly` toggles the F58 AC filter; `from`/`to` bound the date
 * range filter (F59 AC).
 */
export const adminAuditListQuery = listQueryInput(adminAuditSortColumns, {
    defaultSort: 'createdAt',
}).extend({
    actorUserId: uuid.optional(),
    action: auditAction.optional(),
    targetType: z.string().max(120).optional(),
    targetId: z.string().max(120).optional(),
    impersonationOnly: z.boolean().default(false),
    from: timestamp.optional(),
    to: timestamp.optional(),
});
export const adminAuditListResult = paginated(auditEventSchema);
// --- F60 control-all-assets ----------------------------------------------------
export const adminAssetType = z.enum(['document', 'share', 'file']);
export const adminAssetRow = z.object({
    type: adminAssetType,
    id: uuid,
    ownerId: uuid,
    ownerDisplayName: z.string().nullable(),
    title: z.string(),
    /** `shares` rows only; null for a bare `document`/`file` row. */
    moderationStatus: moderationStatus.nullable(),
    preview: previewDescriptor.nullable(),
    createdAt: timestamp,
});
export const adminAssetSortColumns = ['createdAt', 'title'];
export const adminAssetListQuery = listQueryInput(adminAssetSortColumns, {
    defaultSort: 'createdAt',
}).extend({
    type: adminAssetType.optional(),
    ownerId: uuid.optional(),
    moderationStatus: moderationStatus.optional(),
    categoryId: uuid.optional(),
});
export const adminAssetListResult = paginated(adminAssetRow);
/** `admin.assets.unpublish` — reversible (F60 AC: "auditable BOTH directions"); does NOT claw back awarded F73 credits. */
export const adminAssetUnpublishInput = z.object({
    type: z.enum(['document', 'share']),
    id: uuid,
    reason: z.string().trim().min(1, 'errors:admin.reason.required').max(1000, 'errors:admin.reason.tooLong'),
});
export const adminAssetUnpublishResult = z.object({ id: uuid, unpublishedAt: timestamp });
/**
 * `admin.assets.remove` — distinct from moderator F69 takedown
 * (public-content-only, `./moderation.ts` `takedownInput`): this is the
 * admin full override, reaching private assets too. `reasonCategory` is set
 * when the removal doubles as a moderation-verdict override (same closed
 * `policyCategory` vocabulary as every other refusal — F60 AC "never invent
 * moderation categories").
 */
export const adminAssetRemoveInput = z.object({
    type: adminAssetType,
    id: uuid,
    reasonCategory: policyCategory.optional(),
    reason: z.string().trim().min(1, 'errors:admin.reason.required').max(1000, 'errors:admin.reason.tooLong'),
});
export const adminAssetRemoveResult = z.object({ id: uuid, removedAt: timestamp });
// --- F53 contact + DMCA inbox --------------------------------------------------
export const adminContactSortColumns = ['createdAt'];
export const adminContactListQuery = listQueryInput(adminContactSortColumns, {
    defaultSort: 'createdAt',
    defaultDir: 'asc', // oldest-unhandled-first SLA ordering (F53 AC)
}).extend({
    submissionType: submissionType.optional(),
    status: submissionStatus.optional(),
});
export const adminContactListResult = paginated(contactSubmissionSchema);
export const contactHandleAction = z.enum(['assign', 'respond', 'close']);
/** `admin.contact.handle` — `respond` sends via Cloudflare Email Sending (F23) and requires `responseMessage`; `assign`/`close` don't. Always writes `audit_events` (`action='contact.handle'`, F53 AC) — even for an already-removed DMCA target (F37 AC: "still close out"). */
export const adminContactHandleInput = z
    .object({
    submissionId: uuid,
    action: contactHandleAction,
    responseMessage: z.string().trim().max(10000, 'errors:admin.contact.response.tooLong').optional(),
})
    .refine((v) => v.action !== 'respond' || (v.responseMessage?.length ?? 0) > 0, {
    message: 'errors:admin.contact.response.required',
    path: ['responseMessage'],
});
export const adminContactHandleResult = contactSubmissionSchema;
/** Push on `admin:contact-inbox` (§5.5, F53/F38) — a new submission arrived; staff clients refetch `admin.contact.list`. Server authorizes this channel to `isStaff` sockets only (§5.5). */
export const adminContactInboxPush = z.object({
    event: z.literal('submission.created'),
    submissionId: uuid,
    submissionType,
});
// --- F71 analytics (admin-only, cached PG read-model, E1 — NOT a warehouse) --
export const analyticsMetric = z.enum([
    'signups',
    'dau',
    'wau',
    'mau',
    'generation_volume',
    'credit_spend',
    'credit_earn',
    'conversion_free_to_pro',
]);
/** `admin.analytics.overview` — the dashboard headline numbers; no payload. */
export const adminAnalyticsOverviewResult = z.object({
    signupsTotal: z.number().int().nonnegative(),
    dau: z.number().int().nonnegative(),
    wau: z.number().int().nonnegative(),
    mau: z.number().int().nonnegative(),
    /** Grows automatically as new `jobType`s are added (F71 AC) — keyed off the same closed enum `./job.ts` owns. */
    generationByWorker: z.array(z.object({
        workerType: jobType,
        succeeded: z.number().int().nonnegative(),
        failed: z.number().int().nonnegative(),
    })),
    creditSpend: z.number().int().nonnegative(), // sum of negative deltas, absolute
    creditEarn: z.number().int().nonnegative(), // sum of positive (reward) deltas
    freeToProConversionPercent: z.number(),
    signupToFirstDocPercent: z.number(),
    topPublicAssets: z
        .array(z.object({ shareId: uuid, documentTitle: z.string(), viewCount: z.number().int().nonnegative() }))
        .max(20),
    generatedAt: timestamp,
});
/** `admin.analytics.timeseries` — one metric, bucketed over a bounded range. */
export const adminAnalyticsTimeseriesQuery = z.object({
    metric: analyticsMetric,
    from: timestamp,
    to: timestamp,
    granularity: z.enum(['day', 'week']).default('day'),
});
export const analyticsTimeseriesPoint = z.object({ bucket: timestamp, value: z.number() });
export const adminAnalyticsTimeseriesResult = z.object({
    metric: analyticsMetric,
    points: z.array(analyticsTimeseriesPoint),
});
// --- F61 landing CMS block editor -----------------------------------------------
export const adminCmsBlockListQuery = z.object({ locale: localeSchema.optional() });
export const adminCmsBlockListResult = z.object({ blocks: z.array(landingContentSchema) });
/** `admin.cms.blocksSave` — writes a NEW draft version (monotonic per `blockKey`+`locale`, never overwrites, §26). Unsafe HTML is stripped server-side; F61 AC: the response SHOWS what was removed via a diff the caller renders, so `contentJsonb` echoes back the sanitized result. */
export const adminCmsBlockSaveInput = z.object({
    blockKey: z.string().trim().min(1, 'errors:admin.cms.blockKey.required').max(120, 'errors:admin.cms.blockKey.tooLong'),
    locale: localeSchema,
    contentJsonb: jsonObject,
});
export const adminCmsBlockSaveResult = landingContentSchema;
/** `admin.cms.blocksPublish` — flips one specific draft version live; invalidates `landing:{locale}` (E1) + triggers Next ISR revalidate. Also used for the F61 "revert" action (publish an OLDER version number). */
export const adminCmsBlockPublishInput = z.object({
    blockKey: z.string().min(1),
    locale: localeSchema,
    version: z.number().int().positive(),
});
export const adminCmsBlockPublishResult = landingContentSchema;
